import {
  FullLayout,
  LabelerLayout,
  Layout,
  Passthrough,
  ResponsiveLayout,
} from '@robovision/rvai-base'
import { features } from '~services/features'
import {
  projectAlgorithmGuard,
  projectImportDetailsNavigationGuard,
  projectRecordingsNavigationGuard,
  projectTestsNavigationGuard,
} from '../guards/locals/projectGuards'
import { RouteNames } from '../moduleRouteNames/projects'
import type { RvaiRouteRecord } from '../types'


// We need to ignore "imports" for components properties in routes,
// because without it we have no possibility to import (dynamically) pages for specific router.
// Normally we don't need to import something from "pages" into router,
// but eslint doesn't know the difference between normal and dynamic imports.
export const routes: RvaiRouteRecord<RouteNames> = {
  children: [
    {
      children: [
        {
          // eslint-disable-next-line import/no-restricted-paths
          component: () => import('~modules/projects/pages/ProjectNew.vue'),
          meta: {
            allowedLayouts: [Layout.Default],
            can: ['create', 'Project'],
            licensed: true,
          },
          name: RouteNames.ProjectNew,
          path: '',
        },
      ],
      component: ResponsiveLayout,
      path: 'create',
    },
    {
      beforeEnter: projectAlgorithmGuard,
      children: [
        {
          children: [
            {
              // eslint-disable-next-line import/no-restricted-paths
              component: () => import('~modules/projects/pages/ProjectDataImport.vue'),
              meta: {
                can: ['read', 'Import'],
              },
              name: RouteNames.ProjectDataImports,
              path: 'import',
            },
            {
              beforeEnter: projectImportDetailsNavigationGuard,
              // eslint-disable-next-line import/no-restricted-paths
              component: () => import('~modules/projects/pages/ImportDetails.vue'),
              meta: {
                can: ['read', 'Import'],
              },
              name: RouteNames.ProjectDataImportDetails,
              path: 'import/:tagId',
            },
            {
              // eslint-disable-next-line import/no-restricted-paths
              component: () => import('~modules/projects/pages/ProjectDataDataset.vue'),
              meta: {
                can: ['read', 'Dataset'],
              },
              name: RouteNames.ProjectDataDatasets,
              path: 'dataset',
            },
            {
              // eslint-disable-next-line import/no-restricted-paths
              component: () => import('~modules/projects/pages/ProjectDataTags.vue'),
              meta: {
                can: ['read', 'Tag'],
              },
              name: RouteNames.ProjectDataTags,
              path: 'tags',
            },
            {
              // eslint-disable-next-line import/no-restricted-paths
              component: () => import('~modules/projects/pages/ImportDetails.vue'),
              meta: {
                can: ['read', 'Tag'],
              },
              name: RouteNames.ProjectDataTagDetails,
              path: 'tags/:tagId',
            },
          ],
          component: ResponsiveLayout,
          meta: {
            can: ['read', 'Import'],
          },
          name: RouteNames.ProjectData,
          path: 'data',
          redirect: {
            name: RouteNames.ProjectDataImports,
          },
        },
        {
          children: [
            {
              // eslint-disable-next-line import/no-restricted-paths
              component: () => import('~modules/projects/pages/DatasetDetails.vue'),
              name: RouteNames.DatasetDetails,
              path: ':datasetId?',
            },
          ],
          component: LabelerLayout,
          meta: {
            can: ['read', 'Dataset'],
          },
          path: 'datasets',
        },
        {
          children: [
            {
              // eslint-disable-next-line import/no-restricted-paths
              component: () => import('~modules/projects/pages/InferenceSetup.vue'),
              meta: {
                allowedLayouts: [Layout.Default],
                can: ['read', 'Inference'],
                licensed: true,
              },
              name: RouteNames.InferenceSetup,
              path: ':inferenceId?',
            },
          ],
          component: ResponsiveLayout,
          path: 'inference',
        },
        {
          children: [
            {
              // eslint-disable-next-line import/no-restricted-paths
              component: () => import('~modules/projects/pages/ProjectLabeling.vue'),
              name: RouteNames.ProjectLabeling,
              path: '',
            },
          ],
          component: LabelerLayout,
          path: 'labeling',
        },
        {
          children: [
            {
              children: [
                {
                  // eslint-disable-next-line import/no-restricted-paths
                  component: () => import('~modules/projects/pages/DatasetDetails.vue'),
                  meta: {
                    can: ['read', 'Model'],
                  },
                  name: RouteNames.ModelDatasetDetails,
                  path: '',
                }],
              component: LabelerLayout,
              meta: {
                can: ['read', 'Dataset'],
              },
              path: ':modelId/datasets/:datasetId?',
            },
            {
              children: [
                {
                  // eslint-disable-next-line import/no-restricted-paths
                  component: () => import('~modules/projects/pages/ProjectModels.vue'),
                  meta: {
                    allowedLayouts: [Layout.Default],
                    can: ['read', 'Model'],
                  },
                  name: RouteNames.ProjectModels,
                  path: '',
                }],
              component: ResponsiveLayout,
              path: '',
            },
          ],
          component: Passthrough,
          meta: {
            can: ['read', 'Model'],
          },
          path: 'models',
        },
        {
          children: [
            {
              children: [
                {
                  beforeEnter: projectTestsNavigationGuard,
                  // eslint-disable-next-line import/no-restricted-paths
                  component: () => import('~modules/projects/pages/ProjectTestSetup.vue'),
                  meta: {
                    allowedLayouts: [Layout.Default],
                    licensed: true,
                  },
                  name: RouteNames.ProjectTestSetup,
                  path: '',
                },
              ],
              component: ResponsiveLayout,
              meta: {
                can: ['create', 'Test'],
              },
              path: 'new',
            },
            {
              beforeEnter: projectTestsNavigationGuard,
              children: [
                {
                  // eslint-disable-next-line import/no-restricted-paths
                  component: () => import('~modules/projects/pages/ProjectTestCenter.vue'),
                  meta: {
                    allowedLayouts: [Layout.Default],
                  },
                  name: RouteNames.ProjectTestCenter,
                  path: '',
                },
              ],
              component: LabelerLayout,
              path: ':testId?',
            },
          ],
          component: Passthrough,
          meta: {
            can: ['read', 'Test'],
          },
          path: 'tests',
        },
        {
          children: [
            {
              // eslint-disable-next-line import/no-restricted-paths
              component: () => import('~modules/projects/pages/ProjectTraining.vue'),
              meta: {
                allowedLayouts: [Layout.Default],
                licensed: true,
              },
              name: RouteNames.ProjectTraining,
              path: '',
            },
          ],
          component: FullLayout,
          meta: {
            can: ['create', 'Model'],
          },
          path: 'train',
        },
        {
          children: [
            {
              children: [
                {
                  // eslint-disable-next-line import/no-restricted-paths
                  component: () => import('~modules/projects/pages/ProjectInferenceSetup.vue'),
                  meta: {
                    allowedLayouts: [Layout.Default],
                    licensed: true,
                  },
                  name: RouteNames.ProjectInferenceSetup,
                  path: ':inferenceId?',
                },
              ],
              component: ResponsiveLayout,
              meta: {
                can: ['create', 'Inference'],
              },
              path: 'setup',
            },
            {
              children: [
                {
                  // eslint-disable-next-line import/no-restricted-paths
                  component: () => import('~modules/projects/pages/ProjectInferenceCenter.vue'),
                  meta: {
                    allowedLayouts: [Layout.Default],
                    can: ['read', 'Inference'],
                    licensed: true,
                  },
                  name: RouteNames.ProjectInferenceCenter,
                  path: '',
                },
              ],
              component: ResponsiveLayout,
              path: '',
            },
          ],
          component: Passthrough,
          meta: {
            can: ['read', 'Inference'],
          },
          path: 'inferences',
        },
        {
          children: [
            {
              // eslint-disable-next-line import/no-restricted-paths
              component: () => import('~modules/projects/pages/ProjectJobSetup.vue'),
              meta: {
                allowedLayouts: [Layout.Full],
                licensed: true,
              },
              name: RouteNames.ProjectJobSetup,
              path: 'setup',
            },
            {
              children: [
                {
                  // eslint-disable-next-line import/no-restricted-paths
                  component: () => import('~modules/projects/pages/ProjectJobs.vue'),
                  meta: {
                    allowedLayouts: [Layout.Default],
                    licensed: true,
                  },
                  name: RouteNames.ProjectJobs,
                  path: '',
                },
              ],
              component: ResponsiveLayout,
              path: '',
            },
          ],
          component: FullLayout,
          path: 'jobs',
        },
        ...(features.showRecordings
          ? [{
            beforeEnter: projectRecordingsNavigationGuard,
            children: [
              {
                children: [
                  {
                    // eslint-disable-next-line import/no-restricted-paths
                    component: () => import('~modules/projects/pages/ProjectRecordingSetup.vue'),
                    meta: {
                      allowedLayouts: [Layout.Full],
                      licensed: true,
                    },
                    name: RouteNames.ProjectRecordingSetup,
                    path: ':recordingId?',
                  },
                ],
                component: FullLayout,
                meta: {
                  can: ['create', 'Recording'],
                },
                path: 'setup',
              },
              {
                children: [
                  {
                    // eslint-disable-next-line import/no-restricted-paths
                    component: () => import('~modules/projects/pages/ProjectRecordingCenter.vue'),
                    meta: {
                      allowedLayouts: [Layout.Default],
                    },
                    name: RouteNames.ProjectRecordingCenter,
                    path: '',
                  },
                ],
                component: ResponsiveLayout,
                path: '',
              },
            ],
            component: Passthrough,
            meta: {
              can: ['read', 'Recording'],
            },
            path: 'recordings',
          } as RvaiRouteRecord<RouteNames>] : []),
        {
          children: [
            {
              // eslint-disable-next-line import/no-restricted-paths
              component: () => import('~modules/projects/pages/ProjectDetails.vue'),
              meta: {
                allowedLayouts: [Layout.Default],
              },
              name: RouteNames.ProjectDetails,
              path: '',
            },
          ],
          component: ResponsiveLayout,
          meta: {
            can: ['read', 'Project'],
          },
          path: '',
        },
      ],
      // eslint-disable-next-line import/no-restricted-paths
      component: () => import('~modules/projects/components/ProjectRoutesPassthrough.vue'),
      path: ':projectName',
    },
    {
      children: [
        {
          // eslint-disable-next-line import/no-restricted-paths
          component: () => import('~modules/projects/pages/Projects.vue'),
          name: RouteNames.Projects,
          path: '',
        },
      ],
      component: ResponsiveLayout,
      path: '',
    },
  ],
  component: Passthrough,
  path: '/projects',
}
